import React, { useEffect, useRef, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * PublicInstallBadge
 * ──────────────────────────────────────────────────────────────────
 * Slim floating "Install app" badge shown on public-facing routes
 * (Gateway / Landing) across BOTH desktop and mobile.
 *
 * Design rules (zero-layout-change constraint):
 *   • Absolutely positioned bottom-right — never shifts page content
 *   • Matches the existing GlowingReLogo blue-glow visual language
 *   • Only renders when `beforeinstallprompt` fires (Android Chrome,
 *     desktop Chrome/Edge, Brave) OR on iOS Safari (with A2HS hint)
 *   • Self-dismisses for: already-installed PWA (display-mode:standalone),
 *     user-dismissed-this-session, or user-installed-this-session
 *   • No external deps — pure CSS animation
 */
const PublicInstallBadge = () => {
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [iosHintOpen, setIosHintOpen] = useState(false);
  const deferredRef = useRef(null);

  useEffect(() => {
    // Already installed?
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone) return;
    // User already dismissed or installed in this session?
    if (sessionStorage.getItem('public_install_dismissed')) return;
    if (localStorage.getItem('remod_pwa_installed') === '1') return;

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      && !window.MSStream;
    setIsIOS(iOS);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      localStorage.setItem('remod_pwa_installed', '1');
      setVisible(false);
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari never fires beforeinstallprompt — show the instruction
    // tile after a short delay so the user has time to read the page.
    let iosTimer;
    if (iOS) {
      iosTimer = setTimeout(() => setVisible(true), 4000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setIosHintOpen(true);
      return;
    }
    const evt = deferredRef.current;
    if (!evt) return;
    evt.prompt();
    const { outcome } = await evt.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem('remod_pwa_installed', '1');
    }
    setVisible(false);
    deferredRef.current = null;
  };

  const handleDismiss = () => {
    sessionStorage.setItem('public_install_dismissed', '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes pibFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div
        className="fixed bottom-4 right-4 z-[9995] max-w-[320px]"
        style={{ animation: 'pibFadeIn 0.35s ease-out forwards' }}
        data-testid="public-install-badge"
      >
        <div
          className="flex items-center gap-3 rounded-2xl bg-white pl-3 pr-2 py-2 shadow-xl border border-slate-200"
          style={{ boxShadow: '0 8px 28px 4px rgba(96, 165, 250, 0.55), 0 2px 8px rgba(15, 23, 42, 0.18)' }}
        >
          <div
            className="w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0"
            style={{ boxShadow: '0 0 16px 2px rgba(96, 165, 250, 0.75)' }}
          >
            <img src="/remodify-logo-blue.png" alt="" className="h-5 w-auto object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-slate-800 leading-tight">
              Install the Remoddash app
            </p>
            <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
              {isIOS
                ? 'Tap Share → Add to Home Screen'
                : 'One tap. No app-store required.'}
            </p>
          </div>
          <button
            onClick={handleInstall}
            className="px-2.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-semibold inline-flex items-center gap-1 transition-colors"
            data-testid="public-install-badge-btn"
            aria-label="Install Remoddash"
          >
            <Download className="w-3.5 h-3.5" />
            Install
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 text-slate-300 hover:text-slate-600 transition-colors"
            data-testid="public-install-badge-dismiss"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {iosHintOpen && (
        <div
          className="fixed inset-0 z-[9996] bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setIosHintOpen(false)}
          data-testid="public-install-badge-ios-hint"
        >
          <div
            className="bg-white rounded-2xl p-5 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-bold text-slate-900 mb-2">Install on iPhone / iPad</h3>
            <ol className="list-decimal list-inside text-[13px] text-slate-700 space-y-1.5">
              <li>Tap the <b>Share</b> icon at the bottom of Safari.</li>
              <li>Scroll and choose <b>Add to Home Screen</b>.</li>
              <li>Tap <b>Add</b> — you're done.</li>
            </ol>
            <button
              onClick={() => setIosHintOpen(false)}
              className="mt-4 w-full py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold"
              data-testid="public-install-badge-ios-hint-close"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PublicInstallBadge;
